import React, { useEffect, useState } from 'react';
import { Button, Icon, Modal, Popup } from 'semantic-ui-react';
import { useRefreshJob, REFRESH_STATUS, REFRESH_CONNECTION } from './RefreshJobProvider';

const COLORS = {
    [REFRESH_STATUS.CONNECTING]: '#6c757d',
    [REFRESH_STATUS.RUNNING]: '#2185d0',
    [REFRESH_STATUS.COMPLETE]: '#21ba45',
    [REFRESH_STATUS.ERROR]: '#db2828',
};

const ICONS = {
    [REFRESH_STATUS.CONNECTING]: 'plug',
    [REFRESH_STATUS.RUNNING]: 'spinner',
    [REFRESH_STATUS.COMPLETE]: 'check circle',
    [REFRESH_STATUS.ERROR]: 'warning circle',
};

const TITLES = {
    [REFRESH_STATUS.CONNECTING]: 'Connecting to notification service…',
    [REFRESH_STATUS.RUNNING]: 'Refreshing report data',
    [REFRESH_STATUS.COMPLETE]: 'Report refresh complete',
    [REFRESH_STATUS.ERROR]: 'Refresh failed',
};

const SHORT_LABELS = {
    [REFRESH_STATUS.CONNECTING]: 'Connecting',
    [REFRESH_STATUS.RUNNING]: 'Refreshing',
    [REFRESH_STATUS.COMPLETE]: 'Refreshed',
    [REFRESH_STATUS.ERROR]: 'Refresh failed',
};

// Liveness pings go out every 15s; no confirmed reply for this long means the socket looks stalled
const STALE_AFTER_MS = 40 * 1000;

const LIVE_COLORS = {
    live: '#21ba45',
    stale: '#f2711c',
    reconnecting: '#f2711c',
    lost: '#db2828',
};

const formatElapsed = (ms) => {
    const total = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(total / 60);
    const s = String(total % 60).padStart(2, '0');
    return `${m}:${s}`;
};

const isActiveStatus = (status) => status === REFRESH_STATUS.CONNECTING || status === REFRESH_STATUS.RUNNING;

// Ticks every second while the job is active, so elapsed and "last confirmed" stay current
const useNow = (active) => {
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        if (!active) return undefined;
        setNow(Date.now());
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [active]);

    return now;
};

const dotStyles = `
@keyframes refresh-live-ping {
    0%   { box-shadow: 0 0 0 0 rgba(33, 186, 69, 0.6); }
    100% { box-shadow: 0 0 0 8px rgba(33, 186, 69, 0); }
}
@keyframes refresh-live-blink {
    50% { opacity: 0.25; }
}
`;

// Small dot beside the label: pulses green on every confirmed ping, amber/red when the socket is in trouble
const LiveDot = ({ state, pulseKey }) => (
    <span
        key={pulseKey}
        style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            marginLeft: 8,
            background: LIVE_COLORS[state],
            animation: state === 'live'
                ? 'refresh-live-ping 1.2s ease-out 1'
                : state === 'lost' ? 'none' : 'refresh-live-blink 1s step-start infinite',
        }}
    />
);

const getLiveState = (connection, lastAliveAt, now) => {
    if (connection === REFRESH_CONNECTION.LOST) return 'lost';
    if (connection === REFRESH_CONNECTION.RECONNECTING) return 'reconnecting';
    if (lastAliveAt && now - lastAliveAt > STALE_AFTER_MS) return 'stale';
    return 'live';
};

const liveDescription = (liveState, lastAliveAt, latencyMs, pod, now) => {
    if (liveState === 'lost') return 'Connection lost — the result may be missed. Reload the report to check.';
    if (liveState === 'reconnecting') return 'Connection dropped — reconnecting…';
    const ago = lastAliveAt ? `${Math.max(0, Math.round((now - lastAliveAt) / 1000))}s ago` : 'waiting for first check';
    const prefix = liveState === 'stale' ? 'No reply from server recently' : 'Connection live';
    const details = [`last confirmed ${ago}`, latencyMs != null && `${latencyMs} ms`, pod && `pod ${pod}`].filter(Boolean).join(' · ');
    return `${prefix} — ${details}. Checked every 15s.`;
};

// Shown only once the job has finished; the user must acknowledge it
const RefreshResultModal = ({ status, message, elapsed, dismiss }) => {
    const color = COLORS[status];

    return (
        <Modal open size="tiny" onClose={dismiss}>
            <Modal.Header><Icon name={ICONS[status]} style={{ color }} /> {TITLES[status]}</Modal.Header>
            <Modal.Content>
                <p>{message}</p>
                <p style={{ color: '#777' }}>Took {elapsed}</p>
            </Modal.Content>
            <Modal.Actions>
                <Button primary content="OK" onClick={dismiss} />
            </Modal.Actions>
        </Modal>
    );
};

const RefreshJobIndicator = () => {
    const ctx = useRefreshJob();
    const { status, message, startedAt, finishedAt, connection, lastAliveAt, latencyMs, pod, dismiss } = ctx || {};
    const isActive = isActiveStatus(status);
    // Hooks must run before any early return
    const now = useNow(isActive);

    if (!ctx || status === REFRESH_STATUS.IDLE) return null;

    const elapsed = startedAt ? formatElapsed((isActive ? now : (finishedAt || now)) - startedAt) : '0:00';
    const isRunning = status === REFRESH_STATUS.RUNNING;
    const liveState = isRunning ? getLiveState(connection, lastAliveAt, now) : null;
    const tooltip = isRunning
        ? `${TITLES[status]} — ${liveDescription(liveState, lastAliveAt, latencyMs, pod, now)}`
        : (message || TITLES[status]);

    return (
        <>
            <style>{dotStyles}</style>
            <Popup
                trigger={
                    <span
                        style={{ cursor: isActive ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', marginRight: 8 }}
                        onClick={!isActive ? dismiss : undefined}
                    >
                        <Icon name={ICONS[status]} loading={isRunning} style={{ color: COLORS[status], margin: 0 }} size="large" />
                        <span style={{ marginLeft: 6, fontSize: '0.85em', fontWeight: 600, color: COLORS[status], whiteSpace: 'nowrap' }}>
                            {SHORT_LABELS[status]} · <span style={{ fontVariantNumeric: 'tabular-nums' }}>{elapsed}</span>
                        </span>
                        {/* Re-keyed on every confirmed ping so the green pulse replays */}
                        {isRunning && <LiveDot state={liveState} pulseKey={`${liveState}-${lastAliveAt || 0}`} />}
                    </span>
                }
                content={<span>{tooltip}</span>}
                position="bottom right"
                on="hover"
            />
            {!isActive && <RefreshResultModal status={status} message={message} elapsed={elapsed} dismiss={dismiss} />}
        </>
    );
};

export default RefreshJobIndicator;
